import { TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PlainInput } from 'components/PlainInput'
import { SaveChanges } from 'components/SaveChanges'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button } from 'stories/components'
import { Checkbox } from 'stories/components/Checkbox/Checkbox'
import { confirm, generateUniqeId, prompt } from 'utils'

interface IOrderValuationConfig {
  title: string
  visible: boolean
}

const defaultOptions = [
  'AEI Consultants',
  'Appraisal Nation',
  'BBG Real Estate Services',
  'Bowery Valuations',
  'CBRE Commercial Real Estate Services',
  'CFSI Management',
  'Clear Capital',
  'Colliers',
  'Cushman & Wakefield',
  'Elite Valuations',
  'Hillman Consulting',
  'IRR Integra Realty Resources',
  'KOW Building Consultants',
  'Land Gorilla',
  'Nationwide Appraisal Network (NAN)',
  'Nationwide AMC (NAAN)',
  'Newmark Knight Frank',
  'Partners ESI',
  'Richer Values',
  'Trinity',
  'Valuation Services AMC (VSAMC)',
]

export const OrderValuation = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<Record<string, IOrderValuationConfig>>({})
  const [isChanged, setChanged] = useState(false)

  useEffect(() => {
    setLoading(true)
    getAdminConfig('orderValuation')
      .then(setData)
      .finally(() => setLoading(false))
  }, [])

  const onSubmit = async () => {
    setLoading(true)

    setAdminConfig('orderValuation', data)
      .then(() => {
        setChanged(false)
        toast(`Order Valuation Configuration has been saved.`, { type: 'info' })
      })
      .finally(() => setLoading(false))
  }

  const onChange = (key: string, value: any, map: string) => {
    let temp = cloneDeep(data) as any
    if (temp[key] === undefined) temp[key] = {}
    temp[key][map] = value
    setData(temp)
    setChanged(true)
  }

  const onAdd = async () => {
    const title = await prompt('Please enter the new title')
    if (!title) return
    const newData = cloneDeep(data)
    const newKey = generateUniqeId(title, Object.keys(data))
    if (!newKey) return
    newData[newKey] = {
      title,
      visible: true,
    }
    setData(newData)
    setChanged(true)
  }

  const onRemove = async (key: string) => {
    const result = await confirm('Are you sure you want to remove this item?')
    if (!result) return
    const newData = cloneDeep(data)
    delete newData[key]
    setData(newData)
    setChanged(true)
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <SaveChanges show={isChanged} onSave={onSubmit} />
      <div className="mt-4">
        <table className="text-sm">
          <thead className="uppercase text-xs bg-gray-50 dark:bg-gray-700">
            <tr>
              {['No', 'Title', 'In Use', 'Action'].map((v) => (
                <th scope="col" className="p-3 border" key={`header-${v}`}>
                  {v}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(data).map((key, index) => {
              const { title, visible } = data[key]
              const isCustom = !defaultOptions.includes(key)
              return (
                <tr key={index}>
                  <td className="p-2 border">{index + 1}</td>
                  <td className="px-3 border">
                    <PlainInput
                      value={title}
                      content={title}
                      onChange={(value: string) => onChange(key, value, 'title')}
                    />
                  </td>
                  <td className="px-3 border text-center">
                    <Checkbox
                      color="gray"
                      id={key}
                      title={''}
                      key={key}
                      value={visible}
                      onChange={(value) => onChange(key, value, 'visible')}
                      className="w-[none]"
                    />
                  </td>
                  <td className="px-3 border">
                    {isCustom && (
                      <div className="flex">
                        <span className="btn-icon" onClick={() => onRemove(key)}>
                          <TrashIcon className="w-4 h-4 text-red-500" />
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        <Button link onClick={onAdd}>
          + Add
        </Button>
      </div>
    </div>
  )
}
